import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useCreateReport, useReports } from "@/api/queries";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { ChartIcon, DatabaseIcon, PlusIcon, ReportIcon, SearchIcon } from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/** Saved answers (PRD FR-7.1). A report is a thing someone returns to. */
export function ReportsPage() {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const { data: reports, isLoading } = useReports(search);

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col">
      <header className="border-border flex flex-wrap items-center gap-3 border-b px-6 py-4">
        <div className="min-w-0 flex-1">
          <h1 className="type-h2 text-ink">Reports</h1>
          <p className="type-support text-ink-secondary mt-1">
            Pinned answers that refresh when you open them, with the SQL that produced them.
          </p>
        </div>
        <label className="relative">
          <SearchIcon className="text-ink-disabled pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search reports"
            className="bg-surface border-border rounded-element control-md type-support text-ink focus:border-border-emphasized motion-fast w-56 border ps-8 pe-3 outline-none"
          />
        </label>
        <Button
          variant="primary"
          icon={<PlusIcon className="size-4" />}
          onClick={() => setCreating(true)}
        >
          New report
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : reports?.length ? (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reports.map((report) => (
              <li key={report.id}>
                <ReportCard report={report} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<ReportIcon className="size-6" />}
            title={search ? "No reports match that" : "No reports yet"}
            detail={
              search
                ? "Try a different word from the title or the question."
                : "Two ways in: ask a question and use Save as report on the answer, or start an empty one and add tiles from statements you write. Either way every tile refreshes, resizes and re-queries."
            }
            action={
              search ? null : (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button variant="primary" onClick={() => setCreating(true)}>
                    New report
                  </Button>
                  <Link to="/chat" className="text-accent hover:underline">
                    Start a conversation
                  </Link>
                </div>
              )
            }
          />
        )}
      </div>

      <NewReportDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/**
 * An empty report (PRD FR-7.1).
 *
 * The counterpart to "Save as report": a report does not have to begin with an
 * answer. This creates the shell and goes straight to it, because an empty
 * report on a list is not a result — the next thing to do is add a tile, and
 * that lives on the report's own page.
 */
function NewReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const create = useCreateReport();
  const navigate = useNavigate();

  async function submit() {
    setFailure(null);
    try {
      const report = await create.mutateAsync({ title, summary });
      setTitle("");
      setSummary("");
      onClose();
      await navigate({ to: "/reports/$reportId", params: { reportId: report.id } });
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "This report could not be created.",
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New report"
      description="An empty report you fill with tiles — statements you write, or artifacts from any conversation."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => void submit()}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">Title</span>
          <input
            value={title}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Weekly pack"
            className="bg-surface border-border rounded-element control-lg type-body text-ink focus:border-border-emphasized motion-fast w-full border px-3 outline-none"
          />
        </label>
        <Textarea
          label="Description"
          hint="The narrative above the grid. Markdown, and editable later."
          rows={3}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          placeholder="What this pack is for, and who reads it."
        />
        {failure ? (
          <p className="bg-error-subtle text-error type-support rounded-element px-3 py-2">
            {failure}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function ReportCard({
  report,
}: {
  report: ReturnType<typeof useReports>["data"] extends (infer T)[] | undefined ? T : never;
}) {
  const stamp = useRelativeTime(report.last_refreshed_at);
  return (
    <Link to="/reports/$reportId" params={{ reportId: report.id }} className="block">
      <Card className="hover:border-border-emphasized motion-fast h-full">
        <div className="flex flex-col gap-2 px-4 py-4">
          <h2 className="type-h4 text-ink line-clamp-2">{report.title}</h2>
          {report.question ? (
            <p className="type-support text-ink-secondary line-clamp-2">“{report.question}”</p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="neutral" icon={<ChartIcon className="size-3" />}>
              {report.tile_count} tile{report.tile_count === 1 ? "" : "s"}
            </Chip>
            {report.connection_name ? (
              <Chip tone="neutral" icon={<DatabaseIcon className="size-3" />}>
                {report.connection_name}
              </Chip>
            ) : null}
          </div>
          <p className="type-caption text-ink-disabled">
            {stamp ? `refreshed ${stamp}` : "not refreshed since it was saved"}
          </p>
        </div>
      </Card>
    </Link>
  );
}
